import { useState, useRef, useEffect } from "react";
import { Upload, Wand2, Loader2, Download, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";

const BACKGROUNDS = [
    { id: "rustic_wood", label: "Rustic Wood" },
    { id: "luxury_restaurant", label: "Luxury Restaurant" },
    { id: "marble", label: "Marble" },
    { id: "dark_moody", label: "Dark Moody" },
    { id: "outdoor_patio", label: "Outdoor Patio" },
    { id: "kitchen_scene", label: "Kitchen Scene" },
    { id: "bar_environment", label: "Bar" },
    { id: "white_studio", label: "White Studio" },
    { id: "lifestyle_dining", label: "Lifestyle Dining" },
];

const MODELS = [
    { id: "gemini-3.1-flash-image-preview", label: "Gemini Nano Banana" },
    { id: "gpt-image-1", label: "OpenAI GPT Image 1" },
];

export default function BackgroundGenerator() {
    const { user, refresh } = useAuth();
    const navigate = useNavigate();
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [original, setOriginal] = useState(null);
    const [originalLocalUrl, setOriginalLocalUrl] = useState(null);
    const [result, setResult] = useState(null);
    const [bg, setBg] = useState("rustic_wood");
    const [model, setModel] = useState(MODELS[0].id);

    useEffect(() => () => originalLocalUrl && URL.revokeObjectURL(originalLocalUrl), [originalLocalUrl]);

    const handleFile = async (file) => {
        if (!file || !file.type.startsWith("image/")) return;
        if (file.size > 12 * 1024 * 1024) {
            toast.error("Image must be under 12MB");
            return;
        }
        setUploading(true);
        setResult(null);
        try {
            setOriginalLocalUrl(URL.createObjectURL(file));
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setOriginal({ storage_path: data.storage_path });
            toast.success("Image uploaded");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const submit = async () => {
        if (!original) return;
        if ((user?.credits_remaining ?? 0) <= 0) {
            toast.error("No credits left");
            navigate("/pricing");
            return;
        }
        setProcessing(true);
        setResult(null);
        try {
            const { data } = await api.post("/background", { image_path: original.storage_path, background: bg, model });
            setResult(data);
            await refresh();
            toast.success("Background swapped");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
        } finally {
            setProcessing(false);
        }
    };

    const reset = () => {
        if (originalLocalUrl) URL.revokeObjectURL(originalLocalUrl);
        setOriginal(null);
        setOriginalLocalUrl(null);
        setResult(null);
        if (fileRef.current) fileRef.current.value = "";
    };

    return (
        <AppShell subtitle="AI Background Generator" title="Swap any backdrop.">
            {!original ? (
                <div className="dropzone rounded-3xl p-10 md:p-20 text-center" data-testid="bg-dropzone">
                    <Upload className="w-10 h-10 text-orange-400 mx-auto mb-5" strokeWidth={1.5} />
                    <div className="font-heading text-2xl md:text-3xl font-semibold mb-3">Upload the dish photo</div>
                    <div className="text-zinc-500 text-sm mb-6">We keep your plate exactly the same and rebuild the background.</div>
                    <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        disabled={uploading}
                        data-testid="bg-browse-btn"
                        className="btn-magic px-6 py-3 rounded-full text-white font-semibold inline-flex items-center gap-2 disabled:opacity-50"
                    >
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        {uploading ? "Uploading..." : "Browse files"}
                    </button>
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
                </div>
            ) : (
                <div className="grid lg:grid-cols-12 gap-8">
                    <div className="lg:col-span-7">
                        {result ? (
                            <div className="rounded-3xl overflow-hidden border border-white/10 glow-orange">
                                <BeforeAfterSlider beforeSrc={originalLocalUrl} afterSrc={fileUrl(result.enhanced_path)} />
                            </div>
                        ) : (
                            <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-zinc-900 aspect-[4/3]">
                                <img src={originalLocalUrl} alt="Original" className="w-full h-full object-cover" />
                                {processing && (
                                    <div className="absolute inset-0 grid place-items-center bg-black/55 backdrop-blur-sm">
                                        <div className="text-center">
                                            <div className="w-12 h-12 rounded-full btn-magic grid place-items-center mx-auto mb-4 pulse-glow">
                                                <Wand2 className="w-5 h-5 text-white" />
                                            </div>
                                            <div className="font-heading text-xl font-semibold mb-1">Rebuilding the scene...</div>
                                            <div className="text-zinc-400 text-sm">Keeping your plate intact</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="mt-4 flex flex-wrap gap-3">
                            <button onClick={reset} type="button" data-testid="bg-reset-btn" className="px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-sm inline-flex items-center gap-2">
                                <RefreshCcw className="w-3.5 h-3.5" /> Upload another
                            </button>
                            {result && (
                                <a
                                    href={fileUrl(result.enhanced_path)}
                                    download={`foodglow-bg-${result.id}.png`}
                                    data-testid="bg-download-btn"
                                    className="px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-sm inline-flex items-center gap-2"
                                >
                                    <Download className="w-3.5 h-3.5" /> Download
                                </a>
                            )}
                        </div>
                    </div>
                    <div className="lg:col-span-5 space-y-6">
                        <div className="glass-card rounded-2xl p-6">
                            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">Choose backdrop</div>
                            <div className="flex flex-wrap gap-2">
                                {BACKGROUNDS.map((b) => (
                                    <button
                                        key={b.id}
                                        type="button"
                                        onClick={() => setBg(b.id)}
                                        data-testid={`bg-preset-${b.id}`}
                                        className={`px-3.5 py-1.5 rounded-full text-sm border transition ${
                                            bg === b.id ? "bg-orange-500/15 border-orange-500/40 text-orange-300" : "bg-white/3 border-white/10 text-zinc-400 hover:bg-white/8 hover:text-white"
                                        }`}
                                    >
                                        {b.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="glass-card rounded-2xl p-6">
                            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">AI model</div>
                            <div className="grid grid-cols-2 gap-2">
                                {MODELS.map((m) => (
                                    <button
                                        key={m.id}
                                        type="button"
                                        onClick={() => setModel(m.id)}
                                        className={`text-left px-3 py-2 rounded-lg border text-sm transition ${
                                            model === m.id ? "border-orange-500/40 bg-orange-500/8 text-white" : "border-white/10 bg-white/3 text-zinc-400 hover:bg-white/8"
                                        }`}
                                    >
                                        <div className="font-semibold">{m.label}</div>
                                    </button>
                                ))}
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={submit}
                            disabled={processing}
                            data-testid="bg-submit-btn"
                            className="btn-magic w-full py-4 rounded-2xl text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                            {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                            {processing ? "Working magic..." : "Swap background"}
                        </button>
                        <div className="text-center text-xs text-zinc-500">1 credit · {user?.credits_remaining ?? 0} remaining</div>
                    </div>
                </div>
            )}
        </AppShell>
    );
}
